import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  UsersIcon,
  CircleStackIcon,
  ServerIcon,
  CloudArrowUpIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Header } from '@/components/Layout';
import { PageLoader, StatusBadge } from '@/components/common';
import { organizationsApi, databasesApi, metricsApi, storageApi, systemApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import type { Organization, DatabaseInfo, ServerMetrics, ServiceHealth } from '@/types';
import { PERMISSIONS } from '@/types';

interface StorageStats {
  totalBuckets: number;
  totalObjects: number;
  totalSize: number;
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  change?: { value: number; positive: boolean };
  href?: string;
}

function StatCard({ title, value, icon: Icon, change, href }: StatCardProps) {
  const content = (
    <div className="stat-card hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="stat-card-title truncate">{title}</p>
          <p className="stat-card-value truncate">{value}</p>
          {change && (
            <p
              className={`stat-card-change ${
                change.positive ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {change.positive ? (
                <ArrowTrendingUpIcon className="w-4 h-4 mr-1 shrink-0" />
              ) : (
                <ArrowTrendingDownIcon className="w-4 h-4 mr-1 shrink-0" />
              )}
              {change.value}% vs. mes anterior
            </p>
          )}
        </div>
        <div className="p-2 lg:p-3 bg-vida-100 rounded-xl shrink-0">
          <Icon className="w-5 h-5 lg:w-6 lg:h-6 text-vida-600" />
        </div>
      </div>
    </div>
  );

  if (href) {
    return <Link to={href}>{content}</Link>;
  }

  return content;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// --- Disco del servidor (VIDA-309) ------------------------------------------

interface Comando {
  comando: string;
  descripcion: string;
  destructivo?: boolean;
}

interface DiskStatus {
  snapshot: { path: string; total_bytes: number; used_bytes: number; free_bytes: number; used_pct: number };
  decision: { alertar: boolean; motivos: string[]; crecimiento_bytes: number };
  umbrales: { growth_gb: number; min_free_gb: number; max_used_pct: number };
  ultima_alerta_at: string | null;
  historico: { used_bytes: number; used_pct: number; growth_bytes: number; alerted: boolean; createdAt: string }[];
  sugerencias: { diagnostico: Comando[]; limpieza: Comando[]; prohibidos: Comando[]; nota: string };
}

/**
 * Fila de comando con su explicación y botón de copiar. El panel no ejecuta
 * nada: hacerlo exigiría el socket de Docker en el contenedor, que equivale a
 * darle root del host.
 */
function ComandoRow({ c }: { c: Comando }) {
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(c.comando);
      toast.success('Comando copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  return (
    <div className={`rounded-lg border p-3 ${c.destructivo ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
      <div className="flex items-start gap-2">
        <code
          className={`flex-1 text-xs font-mono break-all ${c.destructivo ? 'text-red-700' : 'text-gray-800'}`}
        >
          {c.comando}
        </code>
        {!c.destructivo && (
          <button
            type="button"
            onClick={copiar}
            className="shrink-0 text-xs px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-100 transition-colors"
          >
            Copiar
          </button>
        )}
      </div>
      <p className={`mt-1.5 text-xs ${c.destructivo ? 'text-red-600' : 'text-gray-500'}`}>{c.descripcion}</p>
    </div>
  );
}

function DiskPanel({ disk, onRecheck }: { disk: DiskStatus; onRecheck: () => void }) {
  const [verComandos, setVerComandos] = useState(false);
  const { snapshot, decision, umbrales } = disk;

  // El color sigue al riesgo real, no a un porcentaje bonito: por debajo del
  // umbral de aviso es verde aunque el disco esté a la mitad.
  const critico = snapshot.used_pct > 90 || snapshot.free_bytes < 10 * 1024 ** 3;
  const barra = critico ? 'bg-red-500' : decision.alertar ? 'bg-amber-500' : 'bg-green-500';

  const crecimiento = decision.crecimiento_bytes;

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <h2 className="text-lg font-semibold">Espacio en disco</h2>
        <button
          type="button"
          onClick={onRecheck}
          className="text-sm text-vida-600 hover:text-vida-700"
        >
          Comprobar ahora
        </button>
      </div>

      <div className="p-6 space-y-4">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600">
              {formatBytes(snapshot.used_bytes)} de {formatBytes(snapshot.total_bytes)}
            </span>
            <span className="font-medium">{snapshot.used_pct} %</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className={`h-full ${barra} rounded-full transition-all`} style={{ width: `${snapshot.used_pct}%` }} />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {formatBytes(snapshot.free_bytes)} libres
            {crecimiento !== 0 && (
              <>
                {' · '}
                {crecimiento > 0 ? '+' : '−'}
                {formatBytes(Math.abs(crecimiento))} desde la comprobación anterior
              </>
            )}
          </p>
        </div>

        {decision.alertar ? (
          <div className={`rounded-lg border p-3 ${critico ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`}>
            <p className={`text-sm font-medium ${critico ? 'text-red-800' : 'text-amber-800'}`}>
              {critico ? 'Disco crítico' : 'Aviso de disco'}
            </p>
            <ul className={`mt-1 text-xs list-disc list-inside ${critico ? 'text-red-700' : 'text-amber-700'}`}>
              {decision.motivos.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-xs text-gray-500">
            Sin avisos. Se comprueba cada 6 h y se avisa si crece más de {umbrales.growth_gb} GB entre
            comprobaciones, si quedan menos de {umbrales.min_free_gb} GB libres o si supera el{' '}
            {umbrales.max_used_pct} %.
          </p>
        )}

        <div className="pt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={() => setVerComandos((v) => !v)}
            className="text-sm text-vida-600 hover:text-vida-700"
          >
            {verComandos ? 'Ocultar' : 'Ver'} comandos de diagnóstico y limpieza
          </button>

          {verComandos && (
            <div className="mt-3 space-y-4">
              <p className="text-xs text-gray-500">{disk.sugerencias.nota}</p>

              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-700">Para ver qué ocupa</p>
                {disk.sugerencias.diagnostico.map((c) => (
                  <ComandoRow key={c.comando} c={c} />
                ))}
              </div>

              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-700">
                  Para liberar espacio (no toca volúmenes ni contenedores en marcha)
                </p>
                {disk.sugerencias.limpieza.map((c) => (
                  <ComandoRow key={c.comando} c={c} />
                ))}
              </div>

              <div className="space-y-2">
                <p className="text-xs font-medium text-red-700">No ejecutes nunca</p>
                {disk.sugerencias.prohibidos.map((c) => (
                  <ComandoRow key={c.comando} c={c} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user, hasPermission, hasPlatformPermission } = useAuthStore();
  // Equipo técnico VIDA (VIDA-347): ve servidor, servicios, almacenamiento y disco.
  const canSeeSystem = hasPermission('*') || hasPlatformPermission(PERMISSIONS.PLATFORM_SYSTEM);
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [databases, setDatabases] = useState<DatabaseInfo[]>([]);
  const [storageStats, setStorageStats] = useState<StorageStats | null>(null);
  const [serverMetrics, setServerMetrics] = useState<ServerMetrics | null>(null);
  const [services, setServices] = useState<ServiceHealth[]>([]);
  const [disk, setDisk] = useState<DiskStatus | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const promises: Promise<unknown>[] = [];

      // Load organizations
      if (hasPlatformPermission(PERMISSIONS.PLATFORM_ORGS)) {
        promises.push(
          organizationsApi.list().then((res) => setOrganizations(res.data.organizations))
        );
      } else {
        promises.push(
          organizationsApi.my().then((res) => setOrganizations(res.data.organizations))
        );
      }

      if (canSeeSystem) {
        // Load databases (super admin only)
        if (hasPermission('*')) {
          promises.push(
            databasesApi.list(1, 100).then((res) => setDatabases(res.data.databases || [])).catch((err) => {
              console.error('Error loading databases:', err);
            })
          );
        }

        promises.push(
          metricsApi.server().then((res) => setServerMetrics(res.data)).catch((err) => {
            console.error('Error loading server metrics:', err);
          })
        );

        promises.push(
          metricsApi.services().then((res) => setServices(res.data.services || [])).catch((err) => {
            console.error('Error loading services:', err);
          })
        );

        promises.push(
          storageApi.stats().then((res) => setStorageStats(res.data)).catch((err) => {
            console.error('Error loading storage stats:', err);
          })
        );

        // Disco + comandos de limpieza (VIDA-309). Si falla, el resto del panel
        // sigue funcionando: el bloque simplemente no se pinta.
        promises.push(
          systemApi.getDisk().then((res) => setDisk(res.data)).catch((err) => {
            console.error('Error loading disk status:', err);
          })
        );
      }

      await Promise.all(promises);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fuerza una medición ahora. Guarda histórico y avisa si toca, así que sirve
   * para comprobar el aviso sin esperar a la pasada de las 6 h — por ejemplo
   * justo después de un `docker builder prune -f`.
   */
  const recheckDisk = async () => {
    try {
      await systemApi.runDiskCheck();
      const res = await systemApi.getDisk();
      setDisk(res.data);
      toast.success('Disco comprobado');
    } catch {
      toast.error('No se pudo comprobar el disco');
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  const totalDocs = databases.reduce((acc, db) => acc + (db.stats?.documents || 0), 0);
  const totalSize = databases.reduce((acc, db) => acc + (db.stats?.size_bytes || 0), 0);

  return (
    <div>
      <Header
        title="Dashboard Tech"
        subtitle={`Panel técnico de sistema · ${user?.profile?.first_name || 'Admin'}`}
      />

      <div className="p-6 space-y-6">
        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatCard
            title="Organizaciones"
            value={organizations.length}
            icon={BuildingOfficeIcon}
            href="/organizations"
          />
          <StatCard
            title="Bases de datos"
            value={databases.length}
            icon={CircleStackIcon}
            href="/databases"
          />
          <StatCard
            title="Documentos"
            value={totalDocs.toLocaleString()}
            icon={UsersIcon}
          />
          <StatCard
            title="Buckets"
            value={storageStats?.totalBuckets || 0}
            icon={CloudArrowUpIcon}
            href="/storage"
          />
          <StatCard
            title="Almacenamiento"
            value={formatBytes((storageStats?.totalSize || 0) + totalSize)}
            icon={ServerIcon}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent organizations */}
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h2 className="text-lg font-semibold">Organizaciones recientes</h2>
              <Link to="/organizations" className="text-sm text-vida-600 hover:text-vida-700">
                Ver todas
              </Link>
            </div>
            <div className="divide-y divide-gray-200">
              {organizations.slice(0, 5).map((org) => (
                <div key={org._id} className="px-6 py-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{org.name}</p>
                    <p className="text-sm text-gray-500">{org.slug}</p>
                  </div>
                  <StatusBadge status={org.status} />
                </div>
              ))}
              {organizations.length === 0 && (
                <div className="px-6 py-8 text-center text-gray-500">
                  No hay organizaciones
                </div>
              )}
            </div>
          </div>

          {/* Server status */}
          {canSeeSystem && (
            <div className="card">
              <div className="card-header flex items-center justify-between">
                <h2 className="text-lg font-semibold">Estado del servidor</h2>
                <Link to="/server" className="text-sm text-vida-600 hover:text-vida-700">
                  Ver detalles
                </Link>
              </div>
              <div className="p-6 space-y-4">
                {serverMetrics ? (
                  <>
                    {/* CPU */}
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">CPU</span>
                        <span className="font-medium">{serverMetrics.cpu.usage.toFixed(1)}%</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-vida-500 rounded-full transition-all"
                          style={{ width: `${serverMetrics.cpu.usage}%` }}
                        />
                      </div>
                    </div>

                    {/* Memory */}
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Memoria</span>
                        <span className="font-medium">
                          {formatBytes(serverMetrics.memory.used)} / {formatBytes(serverMetrics.memory.total)}
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-green-500 rounded-full transition-all"
                          style={{ width: `${serverMetrics.memory.usage}%` }}
                        />
                      </div>
                    </div>

                    {/* Disk */}
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Disco</span>
                        <span className="font-medium">
                          {formatBytes(serverMetrics.disk.used)} / {formatBytes(serverMetrics.disk.total)}
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-amber-500 rounded-full transition-all"
                          style={{ width: `${serverMetrics.disk.usage}%` }}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-gray-500 text-center py-4">
                    Métricas no disponibles
                  </p>
                )}

                {/* Services */}
                {services.length > 0 && (
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-sm font-medium text-gray-700 mb-3">Servicios</p>
                    <div className="space-y-2">
                      {services.map((service) => (
                        <div key={service.name} className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">{service.name}</span>
                          <StatusBadge status={service.status} size="sm" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Disco del servidor: tendencia y comandos de limpieza (VIDA-309).
              La barra de "Estado del servidor" da el vistazo rápido; esto añade
              el crecimiento entre comprobaciones y qué ejecutar si sube. */}
          {canSeeSystem && disk && (
            <DiskPanel disk={disk} onRecheck={recheckDisk} />
          )}
        </div>

        {/* Databases list */}
        {databases.length > 0 && (
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h2 className="text-lg font-semibold">Bases de datos</h2>
              <Link to="/databases" className="text-sm text-vida-600 hover:text-vida-700">
                Gestionar
              </Link>
            </div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Organización</th>
                    <th>Base de datos</th>
                    <th>Documentos</th>
                    <th>Tamaño</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {databases.slice(0, 5).map((db) => (
                    <tr key={db.database_name}>
                      <td className="font-medium">{db.organization.name}</td>
                      <td className="text-gray-500 font-mono text-sm">{db.database_name}</td>
                      <td>{db.stats?.documents?.toLocaleString() || '-'}</td>
                      <td>{db.stats ? formatBytes(db.stats.size_bytes) : '-'}</td>
                      <td>
                        <StatusBadge status={db.organization.status} size="sm" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
